import { edgeFunctionHeaders } from '@/lib/edge-functions';
import { supabase } from '@/lib/supabase';

/**
 * /admin/users - the merged roster (formerly /admin/access + /admin/subscriptions).
 *
 * One row per auth user, plus paid-but-not-onboarded `allowed_signups` rows as
 * `kind: 'pending_signup'` pseudo-rows. Search, every filter, the sort and the
 * paging all run in the `admin_list_users` RPC (migration 543) behind the
 * admin-user-access edge function `list` action. The client renders exactly
 * the page the server returns - there is no client-side filtering.
 *
 * Contract: hermes-alfie/docs/claude/specs/admin-users-merge-spec.md
 */

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-user-access`;

export type AdminUserKind = 'user' | 'pending_signup';
export type PlanProduct = 'kc' | 'alfie';
/**
 * Audience filter.
 *
 * `granted` is the word the UI uses (C20): a paid tier or plan with no Stripe
 * subscription behind it. `comp` is its legacy spelling and is identical on the
 * server. `not_paying` is a DIFFERENT set (granted + free) kept only so legacy
 * bookmarks resolve - never wire a "Granted" control to it.
 */
export type UserAudience = 'all' | 'paying' | 'granted' | 'comp' | 'free' | 'not_paying';

/**
 * The derived per-row lifecycle status the `admin_list_users` RPC computes, so
 * roster, record page, CSV and analytics all agree. First-match-wins precedence
 * (see free-floor-model-proposal.md §7.5); never a raw Stripe word.
 */
export type UserStatus =
  | 'not_onboarded'
  | 'on_hold'
  | 'paused'
  | 'suspended'
  | 'past_due'
  | 'cancelling'
  | 'active'
  | 'lapsed'
  | 'free';

/** The status filter ranges over the derived `UserStatus`. */
export type UserStatusFilter = UserStatus;

/** Dropdown order (healthy first, then attention, then ended). */
export const USER_STATUS_ORDER: UserStatus[] = [
  'active',
  'free',
  'past_due',
  'cancelling',
  'on_hold',
  'paused',
  'suspended',
  'lapsed',
  'not_onboarded',
];

/** Display label per derived status - shared by the pill, the filter and the CSV. */
export const USER_STATUS_LABELS: Record<UserStatus, string> = {
  active: 'Active',
  free: 'Free',
  past_due: 'Past due',
  cancelling: 'Cancelling',
  on_hold: 'On hold',
  paused: 'Paused',
  suspended: 'Suspended',
  lapsed: 'Lapsed',
  not_onboarded: 'Not onboarded',
};
/**
 * `joined` and `started` are DIFFERENT dates and both are sortable (mig 545).
 * `joined` orders `created_at` alone - when the account was made. `started`
 * orders COALESCE(plan_started_at, created_at), which dates a payer by their
 * plan, so a 2024 signup who bought in 2026 sorts as a 2026 joiner under it.
 * The Joined column takes `joined`; the Plan started column takes `started`.
 */
export type UserSortKey =
  | 'name'
  | 'email'
  | 'tier'
  | 'plan'
  | 'mrr'
  | 'status'
  | 'joined'
  | 'started'
  /** Real activity (624) - what the Last active column sorts on. */
  | 'last_active'
  /**
   * Auth sign-in. No column sorts on this since 624, but the server still
   * accepts it, so an older bookmarked `?sort=` keeps working.
   */
  | 'last_sign_in';
export type SortDir = 'asc' | 'desc';

export const USER_SORT_KEYS: UserSortKey[] = [
  'name',
  'email',
  'tier',
  'plan',
  'mrr',
  'status',
  'joined',
  'started',
  'last_active',
  'last_sign_in',
];

/**
 * First click on a header sorts by its natural direction: text ascending,
 * numbers and dates descending. `status` ascending is MOST SEVERE FIRST - the
 * server ranks the mig 541 precedence ladder, not the label (do NOT reuse
 * `USER_STATUS_ORDER`, which is a dropdown display order).
 */
export const USER_SORT_NATURAL_DIR: Record<UserSortKey, SortDir> = {
  name: 'asc',
  email: 'asc',
  plan: 'asc',
  status: 'asc',
  tier: 'desc',
  mrr: 'desc',
  joined: 'desc',
  started: 'desc',
  // Most recently active first - the admin is scanning for who has gone quiet,
  // so the interesting end is the one that scrolls into view last.
  last_active: 'desc',
  last_sign_in: 'desc',
};

/**
 * Newest joiners first. `joined` rather than `started` so the default order is
 * the honest account-creation date, and so the sort always names a column that
 * is on by default (D5) - Plan started, which owns `started`, is not.
 */
export const DEFAULT_USER_SORT: UserSortKey = 'joined';
export const DEFAULT_USER_DIR: SortDir = 'desc';

/** Page sizes the roster pager offers. */
export const USERS_PAGE_SIZES = [25, 50, 100, 200] as const;
export type UsersPageSize = (typeof USERS_PAGE_SIZES)[number];
export const DEFAULT_USERS_PAGE_SIZE: UsersPageSize = 25;
/** Server clamp on `limit`; the CSV export pages by this. */
export const USERS_MAX_PAGE_SIZE = 500;

/**
 * `last_active` buckets (624). The four "active within X" ones read real
 * activity - `last_active_at`, i.e. max(activities.created_at) - NOT the auth
 * sign-in. Supabase stamps `last_sign_in_at` only on an actual sign-in and
 * refresh-token sessions last months, so the two are genuinely different
 * signals: a member can be in the app daily on a login from 200 days ago.
 *
 * The four activity buckets OVERLAP by design - a row inside 7d is also inside
 * 10d, 30d and 90d - so they are independent choices, never a partition.
 *
 * The last two are the "not active" pair and are the only ones that still touch
 * the sign-in timestamp: `inactive_90d` is "has an account, no retained
 * activity" (activities are pruned at 90 days, so absence IS 90-day dormancy),
 * and `never` is "never signed in at all", which only the auth record can prove.
 */
export type LastActiveBucket =
  | '7d'
  | '10d'
  | '30d'
  | '90d'
  | 'inactive_90d'
  | 'never';

export const LAST_ACTIVE_BUCKETS: LastActiveBucket[] = [
  '7d',
  '10d',
  '30d',
  '90d',
  'inactive_90d',
  'never',
];

export const LAST_ACTIVE_LABELS: Record<LastActiveBucket, string> = {
  '7d': '7 days',
  '10d': '10 days',
  '30d': '30 days',
  '90d': '90 days',
  inactive_90d: 'No activity 90+ days',
  never: 'Never signed in',
};

/** The tier multi-select's "no membership row" option, as the server takes it. */
export const TIER_NONE = 'none';

/**
 * The plan multi-select's "no plan row at all" option (`plan_code IS NULL`), as
 * the server takes it (mig 545). NOT the `free` audience: that also holds
 * everyone ON the FREE plan, which the roster renders as "Free plan" against
 * this option's "-".
 */
export const PLAN_NONE = 'none';

export interface AdminUserRow {
  /** Null for `pending_signup` pseudo-rows. */
  id: string | null;
  kind: AdminUserKind;
  pending_signup_id: string | null;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string | null;
  last_sign_in_at: string | null;
  /**
   * 624: max(activities.created_at) for this member - the real "Last active".
   * Null means no activity inside the 90-day retention window, which is NOT the
   * same as never active. Always null on `pending_signup` pseudo-rows.
   */
  last_active_at: string | null;

  // Community membership (the user's own K&C seat).
  membership_id: string | null;
  membership_status: string | null;
  tier_code: string | null;
  tier_level: number | null;
  has_addon: boolean;
  /** Seat is stamped by an active hardship hold. */
  on_hardship: boolean;
  /** Exclusive end instant of a scheduled membership removal. */
  removal_due_at: string | null;
  /** The billing org has an active `paused` episode (any source). */
  on_paused: boolean;

  // Billing org.
  organization_id: string | null;
  organization_name: string | null;
  org_role: string | null;
  org_member_count: number;

  // Primary plan (null product = no plan at all).
  plan_product: PlanProduct | null;
  plan_code: string | null;
  plan_name: string | null;
  plan_status: string | null;
  plan_is_comp: boolean;
  plan_mrr_cents: number;
  plan_interval: string | null;
  plan_started_at: string | null;
  plan_stripe_subscription_id: string | null;
  plan_included_alfie_code: string | null;
  /** Alfie plan included with someone else's K&C purchase in the same org. */
  plan_bundled: boolean;

  /** COALESCE(plan_started_at, created_at) - the "Started" column. */
  started_at: string | null;
  /** Active `org_subscription_states.state` codes on the billing org. */
  special_states: string[];
  /**
   * The hold saved to start on a later date for the billing org (hermes migration 710), or null.
   * Attached by the admin-user-access edge function; absent on older payloads.
   */
  scheduled_hold?: { state: string; starts_at: string } | null;
  not_onboarded: boolean;

  /**
   * Derived lifecycle status (§7.5) - the single value the Status pill renders.
   * Raw `plan_status` / `membership_status` above stay for the record page
   * and the roster's "Raw membership status" filter.
   */
  status: UserStatus;
  /** When the derived status began (context for the secondary line). */
  status_since: string | null;
  /** When the derived status ends / resolves (grace end, resume, cancel date). */
  status_until: string | null;
  /** Extra context per status (e.g. previous plan name for `lapsed`). */
  status_detail: string | null;

  // ---- added by migration 543 (the record page's header facts) --------------
  /**
   * Next renewal on the primary plan. Computed since mig 537 and dropped on the
   * way out until 543. Absent on an older edge deployment - treat as null.
   */
  plan_current_period_end?: string | null;
  /** The date the cancel gate enforces. Null when no commitment applies. */
  plan_commitment_ends_at?: string | null;
  /**
   * Null = the onboarding wizard was never finished, which is what the
   * `not_onboarded` status reads. This is the "Waiting since" value.
   */
  onboarding_completed_at?: string | null;
}

export interface PlanCatalogEntry {
  code: string;
  product: PlanProduct;
  name: string;
  price_monthly_cents: number | null;
  included_alfie_code: string | null;
}

/**
 * Cross-filtered counts for every control on the roster.
 *
 * IMPORTANT: an empty group is ABSENT, not zero. With no seatless rows in
 * scope, `facets.tiers.none` is `undefined`, never `0`. Always read with
 * `?? 0`.
 *
 * Migration 543 removed `tier_total`, `none`, `status_total` and `statuses`.
 * The seatless count moved to `tiers['none']`; the raw-status facet is now
 * `membership_status`, keyed on the SEAT's status rather than `plan_status`.
 */
export interface AdminUsersFacets {
  /** Every tier code, PLUS `none` for rows with no membership. */
  tiers: Record<string, number>;
  /** Derived `UserStatus` facet, cross-filtered like the others. */
  status: Record<string, number>;
  audience: {
    all: number;
    paying: number;
    /** A paid tier or plan with no Stripe subscription behind it. The word. */
    granted: number;
    /** Identical to `granted`; the legacy spelling. */
    comp: number;
    /** FREE plan, or no plan at all - the Free floor. */
    free: number;
    /** A DIFFERENT set: granted + free. Never wire the Granted chip to it. */
    not_paying: number;
  };
  pending: number;
  /**
   * What the "Subscription paying only" switch would show with every OTHER filter applied
   * (migration 680): people, and the distinct Stripe subscriptions behind them - the number
   * that matches Revenue -> MRR by tier. Absent from a server that predates 680.
   */
  billing?: { people: number; subscriptions: number };
  /** Every plan code, PLUS `none` for rows with no plan row at all (mig 545). */
  plans: Record<string, number>;
  /**
   * Raw seat status (`user_community_memberships.status`), plus `none` for rows
   * with no membership row. NOT the derived status - see the roster's
   * "Raw membership status" control.
   */
  membership_status: Record<string, number>;
  /**
   * Overlapping buckets, NOT a partition. 7d/10d/30d/90d count real activity;
   * inactive_90d and never count off the sign-in record. See LAST_ACTIVE_BUCKETS.
   */
  last_active: Record<LastActiveBucket, number>;
  plan_catalog: PlanCatalogEntry[];
}

export interface AdminUsersPayload {
  users: AdminUserRow[];
  total: number;
  page: number;
  limit: number;
  /** run_finished_at of the revenue snapshot the billing switch reads (680); null when none. */
  billing_as_of?: string | null;
  facets: AdminUsersFacets;
}

export interface AdminUsersQuery {
  q?: string;
  status?: UserStatusFilter;
  audience?: UserAudience;
  pending?: boolean;
  /** "Subscription paying only" (migration 680). */
  billing?: boolean;
  /** Plan codes, plus `none` for rows with no plan row at all (mig 545). */
  plans?: string[];
  /** Tier codes, plus `none` for rows with no membership (migration 543). */
  tiers?: string[];
  /** RAW `user_community_memberships.status`, plus `none`. Not the derived one. */
  membership_status?: string[];
  /** Inclusive `YYYY-MM-DD` bounds on `created_at`, on Sydney calendar days. */
  joined_from?: string;
  joined_to?: string;
  last_active?: LastActiveBucket;
  sort?: UserSortKey;
  dir?: SortDir;
  page?: number;
  limit?: number;
}

async function authHeaders(): Promise<HeadersInit> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return {
    Authorization: `Bearer ${session.access_token}`,
    'Content-Type': 'application/json',
  };
}

async function readError(res: Response): Promise<string> {
  const body = await res.json().catch(() => null);
  return (body && typeof body.error === 'string' ? body.error : null) ??
    `Request failed (${res.status})`;
}

export function buildUsersListParams(query: AdminUsersQuery): URLSearchParams {
  const params = new URLSearchParams({ action: 'list' });
  if (query.q && query.q.trim() !== '') params.set('search', query.q.trim());
  if (query.status) params.set('status', query.status);
  if (query.audience && query.audience !== 'all') params.set('audience', query.audience);
  if (query.pending) params.set('pending', 'true');
  if (query.billing) params.set('billing', 'true');
  if (query.plans && query.plans.length > 0) params.set('plans', query.plans.join(','));
  if (query.tiers && query.tiers.length > 0) params.set('tiers', query.tiers.join(','));
  if (query.membership_status && query.membership_status.length > 0) {
    params.set('membership_status', query.membership_status.join(','));
  }
  if (query.joined_from) params.set('joined_from', query.joined_from);
  if (query.joined_to) params.set('joined_to', query.joined_to);
  if (query.last_active) params.set('last_active', query.last_active);
  if (query.sort) params.set('sort', query.sort);
  if (query.dir) params.set('dir', query.dir);
  params.set('page', String(query.page ?? 1));
  params.set('limit', String(query.limit ?? DEFAULT_USERS_PAGE_SIZE));
  return params;
}

export async function fetchAdminUsers(query: AdminUsersQuery = {}): Promise<AdminUsersPayload> {
  const headers = await authHeaders();
  const res = await fetch(`${ENDPOINT}?${buildUsersListParams(query).toString()}`, { headers: edgeFunctionHeaders(headers) });
  if (!res.ok) throw new Error(await readError(res));
  return (await res.json()) as AdminUsersPayload;
}

/**
 * Every row matching the query, in the query's sort order, for CSV export.
 * Walks the server pages at the server's maximum page size.
 */
export async function fetchAllAdminUsers(query: AdminUsersQuery = {}): Promise<AdminUserRow[]> {
  const rows: AdminUserRow[] = [];
  for (let page = 1; ; page += 1) {
    const payload = await fetchAdminUsers({ ...query, page, limit: USERS_MAX_PAGE_SIZE });
    rows.push(...payload.users);
    if (payload.users.length < payload.limit || rows.length >= payload.total) return rows;
  }
}

/** Stable identity for a row: the user id, or the pending signup id. */
export function adminUserRowKey(row: AdminUserRow): string {
  return row.id ?? `pending:${row.pending_signup_id ?? row.email}`;
}

// ---- record-page key scheme --------------------------------------------------
// The roster's own selection key is `pending:<allowed_signups_id>`, and the
// server's `get` action takes that form verbatim. A colon inside a PATH segment
// is legal but gets percent-encoded by routers and mangled by copy-paste, so the
// record route uses an underscore instead:
//
//     /admin/users/9f3c1a80-…            a real member (a plain uuid)
//     /admin/users/pending_4b81ff02-…    a paid-but-not-onboarded signup
//
// `recordKeyToApiUserId` is the ONLY place the two spellings meet.

/** URL prefix marking a paid-but-not-onboarded `allowed_signups` row. */
export const PENDING_RECORD_PREFIX = 'pending_';

/** The roster's own selection-key prefix, as the server's `get` action takes it. */
const PENDING_API_PREFIX = 'pending:';

/** The URL segment for a roster row: a uuid, or `pending_<allowed_signups_id>`. */
export function adminUserRecordKey(row: AdminUserRow): string {
  if (row.id) return row.id;
  return `${PENDING_RECORD_PREFIX}${row.pending_signup_id ?? row.email}`;
}

/** A roster `?user=` key (`pending:<id>`) as the record route's path segment. */
export function rosterKeyToRecordKey(key: string): string {
  return key.startsWith(PENDING_API_PREFIX)
    ? `${PENDING_RECORD_PREFIX}${key.slice(PENDING_API_PREFIX.length)}`
    : key;
}

/** The record route's path segment as the `user_id` the edge function takes. */
export function recordKeyToApiUserId(key: string): string {
  return key.startsWith(PENDING_RECORD_PREFIX)
    ? `${PENDING_API_PREFIX}${key.slice(PENDING_RECORD_PREFIX.length)}`
    : key;
}

/** True when the record key names a pending signup rather than a real account. */
export function isPendingRecordKey(key: string): boolean {
  return key.startsWith(PENDING_RECORD_PREFIX);
}

/**
 * One roster row by id - the record page's single-row fetch (`?action=get`,
 * migration 543). The same keys as one entry in `list`.users - both come from
 * admin_user_rows(), so they cannot drift - with no facets, no
 * total and no paging, and it never evaluates the rest of the user base.
 *
 * A READ action, so support_admin reaches it. 404 covers the system user and
 * any `.internal` address, which the roster excludes.
 */
export async function fetchAdminUser(recordKey: string): Promise<AdminUserRow> {
  const headers = await authHeaders();
  const params = new URLSearchParams({
    action: 'get',
    user_id: recordKeyToApiUserId(recordKey),
  });
  const res = await fetch(`${ENDPOINT}?${params.toString()}`, { headers: edgeFunctionHeaders(headers) });
  if (!res.ok) throw new Error(await readError(res));
  const data = (await res.json()) as { user: AdminUserRow };
  return data.user;
}

// ---- audience / lifecycle derivations ----------------------------------------
// Pure helpers the roster, the drawer and the exports all derive from the same
// row fields, so "Free", "Complimentary" and "member" mean the same thing on
// every admin surface. Mirror the `admin_list_users` RPC's audience buckets.

/**
 * The Free floor: a FREE Alfie plan, or no plan row at all. Both render
 * identically (a neutral "Free"), because absence of a plan is the Free floor.
 */
export function isPlanFree(
  row: Pick<AdminUserRow, 'plan_code' | 'plan_product'>,
): boolean {
  return row.plan_code === 'FREE' || row.plan_product === null;
}

/**
 * GRANTED: a paid tier or plan with no Stripe subscription behind it - access
 * an admin gave. Permanent until revoked. Never true for the Free floor, and
 * never rendered as "comp" or "Complimentary" (C20).
 */
export function isPlanGranted(
  row: Pick<AdminUserRow, 'plan_code' | 'plan_product' | 'plan_is_comp'>,
): boolean {
  return !isPlanFree(row) && row.plan_is_comp;
}

/**
 * The community seat grants access only while `active` or in the `past_due`
 * grace window. A `cancelled` / `suspended` / `expired` row is history.
 */
export function isMembershipActive(
  row: Pick<AdminUserRow, 'membership_status'>,
): boolean {
  return row.membership_status === 'active' || row.membership_status === 'past_due';
}
